import { useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { colorsLight, colorsDark, shadows, shadowsDark, radii, spacing, typography } from '../config/theme';

/**
 * useThemeStyles Hook
 *
 * Provides theme-aware style values for use in inline styles.
 * This bridges the gap between CSS variables and React inline styles.
 *
 * For new components, prefer using CSS variables directly via `var(--variable-name)`.
 * Use this hook for complex dynamic styling or when you need programmatic access.
 */

export const useThemeStyles = () => {
  const { isDark } = useTheme();

  const theme = useMemo(() => {
    const colors = isDark ? colorsDark : colorsLight;
    const currentShadows = isDark ? shadowsDark : shadows;

    return {
      // Current mode
      isDark,

      // Colors - flattened for easy access
      colors: {
        // Backgrounds
        bgPrimary: colors.background.primary,
        bgSecondary: colors.background.secondary,
        bgTertiary: colors.background.tertiary,
        bgElevated: colors.background.elevated,
        bgMuted: colors.background.muted,
        bgOverlay: colors.background.overlay,

        // Text
        textPrimary: colors.text.primary,
        textSecondary: colors.text.secondary,
        textTertiary: colors.text.tertiary,
        textMuted: colors.text.muted,
        textDisabled: colors.text.disabled,
        textInverse: colors.text.inverse,

        // Borders
        borderLight: colors.border.light,
        border: colors.border.default,
        borderMedium: colors.border.medium,
        borderStrong: colors.border.strong,

        // Brand
        brandPrimary: colors.brand.primary,
        brandPrimaryHover: colors.brand.primaryHover,
        brandSecondary: colors.brand.secondary,
        brandSecondaryHover: colors.brand.secondaryHover,

        // Status
        success: colors.status.success,
        successLight: colors.status.successLight,
        successText: colors.status.successText,
        warning: colors.status.warning,
        warningLight: colors.status.warningLight,
        warningText: colors.status.warningText,
        error: colors.status.error,
        errorLight: colors.status.errorLight,
        errorText: colors.status.errorText,
        info: colors.status.info,
        infoLight: colors.status.infoLight,
        infoText: colors.status.infoText,

        // Score
        scoreExcellent: colors.score.excellent,
        scoreGood: colors.score.good,
        scoreFair: colors.score.fair,
        scorePoor: colors.score.poor,

        // Weather
        weatherSunny: colors.weather.sunny,
        weatherCloudy: colors.weather.cloudy,
        weatherRain: colors.weather.rain,
        weatherSnow: colors.weather.snow,
        weatherNight: colors.weather.night,

        // Interactive
        hover: colors.interactive.hover,
        active: colors.interactive.active,
        focus: colors.interactive.focus,

        // Chart
        chartBlue: colors.chart.blue,
        chartBlueLight: colors.chart.blueLight,
        chartTrack: colors.chart.track,
        chartMarker: colors.chart.marker,

        // Debug
        debugPurple: colors.debug.purple,
      },

      // Shadows
      shadows: currentShadows,

      // Static values (don't change with theme)
      radii,
      spacing,
      typography,

      // Helper function to get score color
      getScoreColor: (score) => {
        if (score >= 8) return colors.score.excellent;
        if (score >= 6) return colors.score.good;
        if (score >= 4) return colors.score.fair;
        return colors.score.poor;
      },

      // Helper function to get score background
      getScoreBackground: (score) => {
        if (score >= 8) return colors.status.successLight;
        if (score >= 5) return colors.status.warningLight;
        return colors.status.errorLight;
      },

      // Helper to get weather icon color
      getWeatherIconColor: (type) => {
        switch (type) {
          case 'sunny':
          case 'partly-cloudy':
            return colors.weather.sunny;
          case 'cloudy':
          case 'night-cloudy':
          case 'night-partly-cloudy':
            return colors.weather.cloudy;
          case 'rainy':
          case 'rain':
            return colors.weather.rain;
          case 'snowy':
          case 'snow':
          case 'ac_unit':
            return colors.weather.snow;
          case 'night-clear':
            return colors.weather.night;
          default:
            return colors.weather.cloudy;
        }
      },
    };
  }, [isDark]);

  return theme;
};

/**
 * CSS variable reference helper
 *
 * Returns CSS variable references for use in inline styles.
 * These work with the CSS variables defined in index.css.
 */
export const cssVar = {
  // Backgrounds
  bgPrimary: 'var(--bg-primary)',
  bgSecondary: 'var(--bg-secondary)',
  bgTertiary: 'var(--bg-tertiary)',
  bgElevated: 'var(--bg-elevated)',
  bgMuted: 'var(--bg-muted)',
  bgOverlay: 'var(--bg-overlay)',

  // Text
  textPrimary: 'var(--text-primary)',
  textSecondary: 'var(--text-secondary)',
  textTertiary: 'var(--text-tertiary)',
  textMuted: 'var(--text-muted)',
  textDisabled: 'var(--text-disabled)',
  textInverse: 'var(--text-inverse)',

  // Borders
  borderLight: 'var(--border-light)',
  border: 'var(--border)',
  borderMedium: 'var(--border-medium)',
  borderStrong: 'var(--border-strong)',

  // Brand
  brandPrimary: 'var(--brand-primary)',
  brandPrimaryHover: 'var(--brand-primary-hover)',
  brandSecondary: 'var(--brand-secondary)',
  brandSecondaryHover: 'var(--brand-secondary-hover)',

  // Status
  success: 'var(--status-success)',
  successLight: 'var(--status-success-light)',
  successText: 'var(--status-success-text)',
  warning: 'var(--status-warning)',
  warningLight: 'var(--status-warning-light)',
  warningText: 'var(--status-warning-text)',
  error: 'var(--status-error)',
  errorLight: 'var(--status-error-light)',
  errorText: 'var(--status-error-text)',
  info: 'var(--status-info)',
  infoLight: 'var(--status-info-light)',
  infoText: 'var(--status-info-text)',

  // Score
  scoreExcellent: 'var(--score-excellent)',
  scoreGood: 'var(--score-good)',
  scoreFair: 'var(--score-fair)',
  scorePoor: 'var(--score-poor)',

  // Weather
  weatherSunny: 'var(--weather-sunny)',
  weatherCloudy: 'var(--weather-cloudy)',
  weatherRain: 'var(--weather-rain)',
  weatherSnow: 'var(--weather-snow)',

  // Interactive
  hover: 'var(--interactive-hover)',
  active: 'var(--interactive-active)',
  focus: 'var(--interactive-focus)',

  // Chart
  chartBlue: 'var(--chart-blue)',
  chartBlueLight: 'var(--chart-blue-light)',
  chartTrack: 'var(--chart-track)',
  chartMarker: 'var(--chart-marker)',

  // Shadows
  shadowSm: 'var(--shadow-sm)',
  shadow: 'var(--shadow)',
  shadowMd: 'var(--shadow-md)',
  shadowLg: 'var(--shadow-lg)',

  // Overlays - black-based
  overlaySubtle: 'var(--overlay-subtle)',
  overlayLight: 'var(--overlay-light)',
  overlayMedium: 'var(--overlay-medium)',
  overlayStrong: 'var(--overlay-strong)',

  // Overlays - white-based
  overlayWhiteSubtle: 'var(--overlay-white-subtle)',
  overlayWhiteLight: 'var(--overlay-white-light)',
  overlayWhiteMedium: 'var(--overlay-white-medium)',

  // Status overlays (for backgrounds)
  statusSuccessOverlay: 'var(--status-success-overlay)',
  statusWarningOverlay: 'var(--status-warning-overlay)',
  statusErrorOverlay: 'var(--status-error-overlay)',
  statusInfoOverlay: 'var(--status-info-overlay)',

  // Score overlays (for backgrounds)
  scoreExcellentOverlay: 'var(--score-excellent-overlay)',
  scoreGoodOverlay: 'var(--score-good-overlay)',
  scorePoorOverlay: 'var(--score-poor-overlay)',

  // Typography - Font families
  fontSerif: 'var(--font-serif)',
  fontSans: 'var(--font-sans)',
  fontHeading: 'var(--font-heading)',

  // Typography - Font sizes
  text2xs: 'var(--text-2xs)',   // 10px
  textXs: 'var(--text-xs)',     // 11px
  textSm: 'var(--text-sm)',     // 12px
  textBase: 'var(--text-base)', // 14px
  textMd: 'var(--text-md)',     // 16px
  textLg: 'var(--text-lg)',     // 18px
  textXl: 'var(--text-xl)',     // 20px
  text2xl: 'var(--text-2xl)',   // 24px
  text3xl: 'var(--text-3xl)',   // 29px
  text4xl: 'var(--text-4xl)',   // 36px
  textDisplay: 'var(--text-display)',     // 120px
  textDisplayLg: 'var(--text-display-lg)', // 140px

  // Spacing
  space1: 'var(--space-1)',
  space2: 'var(--space-2)',
  space3: 'var(--space-3)',
  space4: 'var(--space-4)',
  space5: 'var(--space-5)',
  space6: 'var(--space-6)',
  space8: 'var(--space-8)',
  space10: 'var(--space-10)',
  space12: 'var(--space-12)',

  // Radii
  radiusSm: 'var(--radius-sm)',
  radius: 'var(--radius)',
  radiusMd: 'var(--radius-md)',
  radiusLg: 'var(--radius-lg)',
  radiusFull: 'var(--radius-full)',

  // Transitions
  transitionFast: 'var(--transition-fast)',
  transition: 'var(--transition)',
  transitionSlow: 'var(--transition-slow)',

  // Z-index
  zDropdown: 'var(--z-dropdown)',   // 100
  zSticky: 'var(--z-sticky)',       // 200
  zModal: 'var(--z-modal)',         // 1000
  zTooltip: 'var(--z-tooltip)',     // 9000
  zToast: 'var(--z-toast)',         // 9500
};

export default useThemeStyles;
